import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';

// TypeScript interfaces
interface SplitSlideHeaderProps {
  className?: string;
  delay?: number;
}

interface AnimationState {
  isLoaded: boolean;
  isHovered: boolean;
  animationStep: 'initial' | 'leftSlide' | 'leftScale' | 'rightSlide' | 'complete';
}

interface AnimationTiming {
  leftSlideIn: number;
  leftScale: number;
  rightSlideIn: number;
  totalDuration: number;
}

const SplitSlideHeader: React.FC<SplitSlideHeaderProps> = ({ 
  className = '', 
  delay = 300 
}) => {
  const [animationState, setAnimationState] = useState<AnimationState>({
    isLoaded: false,
    isHovered: false,
    animationStep: 'initial'
  });

  const controls = useAnimation();

  // Animation timing configuration
  const timing: AnimationTiming = {
    leftSlideIn: 300,
    leftScale: 200,
    rightSlideIn: 300,
    totalDuration: 800
  };

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Sequential animation sequence
  useEffect(() => {
    if (prefersReducedMotion) {
      setAnimationState(prev => ({ 
        ...prev, 
        isLoaded: true, 
        animationStep: 'complete'
      }));
      return;
    }

    const timer = setTimeout(() => {
      // Step 1: Left part slides in
      setAnimationState(prev => ({ ...prev, animationStep: 'leftSlide' }));
      
      // Step 2: Left part scales up
      setTimeout(() => {
        setAnimationState(prev => ({ ...prev, animationStep: 'leftScale' }));
      }, timing.leftSlideIn);
      
      // Step 3: Right part slides in
      setTimeout(() => {
        setAnimationState(prev => ({ ...prev, animationStep: 'rightSlide' }));
      }, timing.leftSlideIn + timing.leftScale);
      
      // Step 4: Complete
      setTimeout(() => {
        setAnimationState(prev => ({ ...prev, animationStep: 'complete', isLoaded: true }));
      }, timing.totalDuration);
      
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, timing.leftSlideIn, timing.leftScale, timing.totalDuration, prefersReducedMotion]);

  // Animation variants for sequential animation
  const leftPartVariants = {
    initial: {
      x: prefersReducedMotion ? 0 : -100,
      opacity: prefersReducedMotion ? 1 : 0,
      scale: 1,
    },
    leftSlide: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: prefersReducedMotion ? 0.1 : timing.leftSlideIn / 1000,
        ease: [0.25, 0.46, 0.45, 0.94], // Smooth easing, not elastic
      }
    },
    leftScale: {
      x: 0,
      opacity: 1,
      scale: prefersReducedMotion ? 1 : 1.1,
      transition: {
        duration: prefersReducedMotion ? 0.1 : timing.leftScale / 1000,
        ease: [0.25, 0.46, 0.45, 0.94],
      }
    },
    rightSlide: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: prefersReducedMotion ? 0.1 : timing.leftScale / 1000,
        ease: [0.25, 0.46, 0.45, 0.94],
      }
    },
    complete: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    hover: {
      y: prefersReducedMotion ? 0 : -2,
      scale: prefersReducedMotion ? 1 : 1.02,
      transition: {
        duration: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94],
      }
    },
    tap: {
      y: prefersReducedMotion ? 0 : 1,
      scale: prefersReducedMotion ? 1 : 0.98,
      transition: {
        duration: 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      }
    }
  };

  const rightPartVariants = {
    initial: {
      x: prefersReducedMotion ? 0 : 100,
      opacity: prefersReducedMotion ? 1 : 0,
    },
    leftSlide: {
      x: prefersReducedMotion ? 0 : 100,
      opacity: prefersReducedMotion ? 1 : 0,
    },
    leftScale: {
      x: prefersReducedMotion ? 0 : 100,
      opacity: prefersReducedMotion ? 1 : 0,
    },
    rightSlide: {
      x: 0,
      opacity: 1,
      transition: {
        duration: prefersReducedMotion ? 0.1 : timing.rightSlideIn / 1000,
        ease: [0.25, 0.46, 0.45, 0.94], // Smooth easing, not elastic
      }
    },
    complete: {
      x: 0,
      opacity: 1,
    },
    hover: {
      y: prefersReducedMotion ? 0 : -2,
      scale: prefersReducedMotion ? 1 : 1.02,
      transition: {
        duration: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94],
      }
    },
    tap: {
      y: prefersReducedMotion ? 0 : 1,
      scale: prefersReducedMotion ? 1 : 0.98,
      transition: {
        duration: 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      }
    }
  };



  return (
    <motion.div
      className={`relative inline-block cursor-pointer ${className}`}
      initial="initial"
      whileHover="hover"
      whileTap="tap"
    >
      {/* Main text container - PS2 style */}
      <motion.div 
        className="relative flex items-center"
        whileHover={{
          scale: prefersReducedMotion ? 1 : 1.1,
          transition: {
            duration: 0.2,
            ease: "easeOut",
            type: "spring",
            stiffness: 300,
            damping: 20
          }
        }}
        whileTap={{
          scale: prefersReducedMotion ? 1 : 0.95,
          transition: {
            duration: 0.1,
            ease: "easeOut"
          }
        }}
      >
        {/* Left part: "0x" */}
        <motion.span
          variants={leftPartVariants}
          initial="initial"
          animate={animationState.animationStep}
          className="text-2xl font-light tracking-wide text-gray-900 dark:text-white relative"
          whileHover={{
            rotate: prefersReducedMotion ? 0 : -2,
            transition: {
              duration: 0.15,
              ease: "easeOut"
            }
          }}
        >
          0x
        </motion.span>

        {/* Right part: "slund" */}
        <motion.span
          variants={rightPartVariants}
          initial="initial"
          animate={animationState.animationStep}
          className="text-2xl font-light tracking-wide text-gray-900 dark:text-white relative"
          whileHover={{
            rotate: prefersReducedMotion ? 0 : 2,
            transition: {
              duration: 0.15,
              ease: "easeOut"
            }
          }}
        >
          slund
        </motion.span>
      </motion.div>

      {/* Elegant underline effect */}
      <motion.div
        className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-transparent via-gray-400 to-transparent dark:via-gray-500"
        initial={{ width: 0, opacity: 0 }}
        whileHover={{ 
          width: "100%", 
          opacity: 1,
          transition: {
            duration: 0.5,
            ease: [0.25, 0.46, 0.45, 0.94],
          }
        }}
      />

      {/* Subtle text shadow effect */}
      <motion.div
        className="absolute inset-0 text-2xl font-light tracking-wide text-gray-900 dark:text-white opacity-0"
        initial={{ opacity: 0, y: 0 }}
        whileHover={{ 
          opacity: 0.1,
          y: 2,
          transition: {
            duration: 0.3,
            ease: [0.25, 0.46, 0.45, 0.94],
          }
        }}
      >
        0xslund
      </motion.div>

      {/* PS2-style floating bounce effect */}
      <motion.div
        className="absolute inset-0"
        whileHover={{
          y: [0, -2, 0],
          transition: {
            duration: 0.6,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "reverse"
          }
        }}
      />
    </motion.div>
  );
};

export default SplitSlideHeader;
